/**
 * FeedbackWidget.js
 * =================
 * Floating feedback button + modal for logged-in users
 *
 * Features:
 * - Floating button (desktop) with X to dismiss
 * - Footer icon link (mobile + desktop)
 * - Auto-popup after benchmark OR every 3+ days
 * - Tabs: Rating / Feature Request / Bug Report
 * - Toast confirmation on submit
 */

import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  X,
  Star,
  Lightbulb,
  Bug,
  Send
} from 'lucide-react';
import { showToast } from './Toast';
import { trackEvent } from '../utils/GoogleAnalytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// ==========================================
// CONSTANTS
// ==========================================
const STORAGE_KEYS = {
  LAST_SHOWN: 'feedback_last_shown',
  LAST_SUBMITTED: 'feedback_last_submitted',
  FLOATING_DISMISSED: 'feedback_floating_dismissed',
  DISMISSED_COUNT: 'feedback_dismissed_count'
};

const DAYS_BETWEEN_POPUPS = 3;
const DAYS_AFTER_DISMISS = 7;
const MAX_DISMISSALS_BEFORE_STOP = 5;

// ==========================================
// RATING EMOJIS
// ==========================================
const RATINGS = [
  { value: 1, emoji: '😢', label: 'Very Bad', color: 'text-red-500 hover:bg-red-50' },
  { value: 2, emoji: '😕', label: 'Bad', color: 'text-orange-500 hover:bg-orange-50' },
  { value: 3, emoji: '😐', label: 'Okay', color: 'text-yellow-500 hover:bg-yellow-50' },
  { value: 4, emoji: '😊', label: 'Good', color: 'text-green-500 hover:bg-green-50' },
  { value: 5, emoji: '😍', label: 'Amazing!', color: 'text-pink-500 hover:bg-pink-50' },
];

// ==========================================
// FEEDBACK MODAL COMPONENT
// ==========================================
function FeedbackModal({ isOpen, onClose, user }) {
  const [activeTab, setActiveTab] = useState('rating');
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('rating');
      setRating(0);
      setTitle('');
      setMessage('');
      trackEvent('Feedback', 'Open Modal');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (activeTab === 'rating' && rating === 0) {
      showToast('Please select a rating', 'warning');
      return;
    }
    if ((activeTab === 'feature' || activeTab === 'bug') && !title.trim()) {
      showToast('Please enter a title', 'warning');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: activeTab,
          rating: activeTab === 'rating' ? rating : null,
          title: title.trim() || null,
          message: message.trim() || null,
          page_url: window.location.pathname
        })
      });

      if (response.ok) {
        showToast('Thanks for your feedback! 🙏', 'success');
        trackEvent('Feedback', 'Submit', activeTab, rating || 0);
        localStorage.setItem(STORAGE_KEYS.LAST_SUBMITTED, Date.now().toString());
        localStorage.setItem(STORAGE_KEYS.LAST_SHOWN, Date.now().toString());
        onClose();
      } else {
        const data = await response.json();
        showToast(data.detail || 'Failed to send feedback', 'error');
      }
    } catch (error) {
      console.error('Feedback error:', error);
      showToast('Failed to send feedback. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'rating', label: 'Rating', icon: Star },
    { id: 'feature', label: 'Feature', icon: Lightbulb },
    { id: 'bug', label: 'Bug', icon: Bug },
  ];

  return (
    <>
      {/* Backdrop - Light overlay */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] animate-fadeIn"
        onClick={onClose}
      />

      {/* Modal Container - Equal padding all sides */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
        style={{ padding: '24px' }}
      >
        {/* Modal Card - Fixed size */}
        <div
          className="bg-white rounded-2xl shadow-xl w-full pointer-events-auto animate-slideUp border border-gray-200"
          style={{ maxWidth: '420px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl">
                <MessageSquare className="text-blue-600" size={20} />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Send Feedback</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  trackEvent('Feedback', 'Switch Tab', tab.id);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <tab.icon size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content - FIXED HEIGHT for all tabs */}
          <div className="p-4" style={{ height: '260px' }}>
            {/* Rating Tab */}
            {activeTab === 'rating' && (
              <div className="h-full flex flex-col">
                <p className="text-center text-gray-600 text-sm mb-3">
                  How's your experience with MidoLABS.ai?
                </p>

                {/* Emoji Rating */}
                <div className="flex justify-center items-center gap-2 py-2">
                  {RATINGS.map((r) => (
                    <button
                      key={r.value}
                      onClick={() => setRating(r.value)}
                      onMouseEnter={() => setHoveredRating(r.value)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className={`p-2 rounded-xl text-2xl transition-all transform ${
                        rating === r.value
                          ? 'bg-blue-100 scale-110 ring-2 ring-blue-400'
                          : r.color
                      } ${hoveredRating === r.value ? 'scale-110' : ''}`}
                      title={r.label}
                    >
                      {r.emoji}
                    </button>
                  ))}
                </div>

                {/* Rating Label */}
                <p className="text-center text-sm font-medium text-gray-700 h-5 mb-3">
                  {rating > 0 ? RATINGS.find(r => r.value === rating)?.label : ''}
                </p>

                {/* Optional Message */}
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us more (optional)..."
                  className="w-full flex-1 p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none text-sm"
                />
              </div>
            )}

            {/* Feature Tab */}
            {activeTab === 'feature' && (
              <div className="h-full flex flex-col">
                <p className="text-center text-gray-600 text-sm mb-3">
                  What feature would make MidoLABS.ai better?
                </p>

                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Feature title..."
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm mb-3"
                  maxLength={100}
                />

                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your idea..."
                  className="w-full flex-1 p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none text-sm"
                />
              </div>
            )}

            {/* Bug Tab */}
            {activeTab === 'bug' && (
              <div className="h-full flex flex-col">
                <p className="text-center text-gray-600 text-sm mb-3">
                  Found a bug? Help us fix it!
                </p>

                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What went wrong?..."
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm mb-3"
                  maxLength={100}
                />

                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Steps to reproduce, what you expected..."
                  className="w-full flex-1 p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none text-sm"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 rounded-b-2xl flex justify-end space-x-3 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center space-x-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium text-sm hover:shadow-lg transition-all disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send size={16} />
                  <span>Send</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
}

// ==========================================
// MAIN FEEDBACK WIDGET COMPONENT
// ==========================================
export default function FeedbackWidget({
  user,
  triggerAfterBenchmark = false,
  isOpen = false,
  onClose
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  const shouldShowFloatingButton = () => {
    if (!user) return false;

    const dismissedAt = localStorage.getItem(STORAGE_KEYS.FLOATING_DISMISSED);
    if (dismissedAt) {
      const daysSinceDismiss = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < DAYS_AFTER_DISMISS) {
        return false;
      }
    }

    const dismissCount = parseInt(localStorage.getItem(STORAGE_KEYS.DISMISSED_COUNT) || '0');
    if (dismissCount >= MAX_DISMISSALS_BEFORE_STOP) {
      return false;
    }

    return true;
  };

  const shouldAutoPopup = () => {
    const lastShown = localStorage.getItem(STORAGE_KEYS.LAST_SHOWN);
    if (lastShown) {
      const daysSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      if (daysSince < DAYS_BETWEEN_POPUPS) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (!user) return;
    setShowFloatingButton(shouldShowFloatingButton());
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      setIsModalOpen(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!user) return;

    if (triggerAfterBenchmark && shouldAutoPopup()) {
      const timer = setTimeout(() => {
        setIsModalOpen(true);
        localStorage.setItem(STORAGE_KEYS.LAST_SHOWN, Date.now().toString());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, triggerAfterBenchmark]);

  const handleClose = () => {
    setIsModalOpen(false);
    if (onClose) onClose();
  };

  const handleDismissFloating = (e) => {
    e.stopPropagation();
    setShowFloatingButton(false);
    localStorage.setItem(STORAGE_KEYS.FLOATING_DISMISSED, Date.now().toString());
    const dismissCount = parseInt(localStorage.getItem(STORAGE_KEYS.DISMISSED_COUNT) || '0');
    localStorage.setItem(STORAGE_KEYS.DISMISSED_COUNT, (dismissCount + 1).toString());
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  if (!user) return null;

  return (
    <>
      {/* Floating Button - Desktop only */}
      {showFloatingButton && (
        <div className="fixed bottom-6 right-6 z-[9990] hidden sm:flex items-center group">
          <button
            onClick={handleOpenModal}
            className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            title="Send Feedback"
          >
            <MessageSquare size={20} />
            <span className="font-medium text-sm">Feedback</span>
          </button>

          <button
            onClick={handleDismissFloating}
            className="absolute -top-2 -right-2 p-1 bg-gray-200 hover:bg-gray-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
            title="Don't show again"
          >
            <X size={14} className="text-gray-600" />
          </button>
        </div>
      )}

      {/* Modal */}
      <FeedbackModal
        isOpen={isModalOpen}
        onClose={handleClose}
        user={user}
      />
    </>
  );
}

// ==========================================
// FEEDBACK ICON FOR FOOTER
// ==========================================
export function FeedbackIcon({ onClick, className = "" }) {
  return (
    <button
      onClick={onClick}
      className={`text-gray-500 hover:text-blue-600 transition-colors ${className}`}
      title="Send Feedback"
      aria-label="Send feedback"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    </button>
  );
}